'use client'

import { useRef, useState } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import type { StorageAllocation } from '@garage/shared'
import { IconButton, AddIcon, DeleteIcon, Spinner, useSnackbar } from '@/ui'
import { api, ApiError } from '@/lib/api.js'
import { qk } from '@/lib/query-keys.js'
import { PhotoLightbox } from './PhotoLightbox.js'

interface AllocationPhotoProps {
  allocation: StorageAllocation
  /** 'md' for the previous tile-header size; 'lg' for the item detail view. */
  size?: 'md' | 'lg'
}

/**
 * Photos of the actual item — as many as it takes (front, a damage
 * close-up, the box it came in). Each is its own tile: uploading adds
 * another rather than replacing what's there, and each can be removed on
 * its own. Clicking any thumbnail opens it full size, with prev/next
 * through the rest of that item's photos.
 */
export function AllocationPhoto({ allocation, size = 'md' }: AllocationPhotoProps) {
  const tileClass = size === 'lg' ? 'h-28' : 'h-20'
  const queryClient = useQueryClient()
  const snackbar = useSnackbar()
  const [lightboxIndex, setLightboxIndex] = useState<number | null>(null)
  const inputRef = useRef<HTMLInputElement>(null)

  const uploadMutation = useMutation({
    mutationFn: (file: File) => api.storage.addItemPhoto(allocation.id, file),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: qk.storageTree })
      snackbar.success('Photo added')
    },
    onError: (error) => snackbar.error(error instanceof ApiError ? error.message : 'Upload failed'),
  })

  const removeMutation = useMutation({
    mutationFn: (photoId: string) => api.storage.removeItemPhoto(allocation.id, photoId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: qk.storageTree })
      snackbar.success('Photo removed')
    },
    onError: (error) => snackbar.error(error instanceof ApiError ? error.message : 'Failed to remove photo'),
  })

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.currentTarget.files?.[0]
    if (!file) return
    uploadMutation.mutate(file)
    e.currentTarget.value = ''
  }

  const photoIds = allocation.photos.map((p) => p.id)

  return (
    <div className="flex flex-wrap gap-2">
      {allocation.photos.map((photo, i) => (
        <div key={photo.id} className={`relative w-28 ${tileClass} group flex-shrink-0`}>
          <button
            type="button"
            onClick={() => setLightboxIndex(i)}
            className="block w-full h-full rounded-md overflow-hidden border border-outline-variant interactive"
          >
            <img
              src={api.storage.itemPhotoUrl(allocation.id, photo.id, 320)}
              alt={allocation.itemName}
              className="w-full h-full object-cover"
              loading="lazy"
              decoding="async"
            />
          </button>
          {/*
           * A corner control, not a full-tile overlay: an overlay centred on
           * the whole thumbnail would sit exactly on top of the click target
           * that opens the lightbox, so a click meant to enlarge the photo
           * would delete it instead.
           */}
          <IconButton
            icon={<DeleteIcon className="w-3.5 h-3.5" />}
            label="Remove photo"
            variant="filled"
            size="sm"
            className="absolute top-1 end-1 opacity-0 group-hover:opacity-100 transition-opacity"
            onClick={(e) => {
              e.stopPropagation()
              removeMutation.mutate(photo.id)
            }}
            disabled={removeMutation.isPending}
          />
        </div>
      ))}

      <div className={`w-28 ${tileClass} flex-shrink-0`}>
        {/* display:none + ref.click(), not sr-only: see ExcelImportField. */}
        <input
          ref={inputRef}
          type="file"
          accept="image/png,image/jpeg,image/webp"
          onChange={handleChange}
          className="hidden"
          disabled={uploadMutation.isPending}
        />
        <button
          type="button"
          onClick={() => inputRef.current?.click()}
          disabled={uploadMutation.isPending}
          className="w-full h-full flex flex-col items-center justify-center gap-1 rounded-md border border-dashed border-outline-variant text-on-surface-variant interactive hover:bg-surface-high cursor-pointer"
        >
          {uploadMutation.isPending ? (
            <Spinner size="sm" />
          ) : (
            <>
              <AddIcon className="w-5 h-5" />
              <span className="text-body-sm">Add photo</span>
            </>
          )}
        </button>
      </div>

      <PhotoLightbox
        open={lightboxIndex !== null}
        photoIds={photoIds}
        index={lightboxIndex ?? 0}
        onIndexChange={setLightboxIndex}
        onClose={() => setLightboxIndex(null)}
        urlFor={(photoId) => api.storage.itemPhotoUrl(allocation.id, photoId, 1600)}
        alt={allocation.itemName}
      />
    </div>
  )
}
